import { clsx } from 'clsx'
import type { LucideIcon } from 'lucide-react'

function StatusDot({ status }: { status: 'ok' | 'error' | 'unknown' }): React.JSX.Element {
  return (
    <span
      className={clsx(
        'inline-block h-3 w-3 rounded-full',
        status === 'ok' && 'bg-green-500',
        status === 'error' && 'bg-red-500',
        status === 'unknown' && 'bg-gray-400',
      )}
    />
  )
}

export function ServiceCard({
  name,
  description,
  status,
  hasHealthEndpoint,
}: {
  name: string
  description: string
  status: 'ok' | 'error' | 'unknown'
  hasHealthEndpoint: boolean
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <StatusDot status={status} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-text-primary">{name}</p>
          <p className="text-xs text-text-tertiary">{description}</p>
        </div>
      </div>
      {!hasHealthEndpoint && (
        <p className="mt-2 text-xs text-text-tertiary italic">
          No direct health endpoint (internal service)
        </p>
      )}
      {hasHealthEndpoint && (
        <p className="mt-2 text-xs text-text-tertiary">
          {status === 'ok'
            ? 'Responding normally'
            : status === 'error'
              ? 'Not responding'
              : 'Checking...'}
        </p>
      )}
    </div>
  )
}

export function DependencyCard({
  name,
  icon: Icon,
  status,
  latencyMs,
}: {
  name: string
  icon: LucideIcon
  status: 'ok' | 'error' | 'unknown'
  latencyMs: number | null
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-text-secondary" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-text-primary">{name}</p>
            <StatusDot status={status} />
          </div>
          {latencyMs !== null ? (
            <p className="mt-0.5 text-xs text-text-tertiary">{latencyMs.toFixed(1)}ms latency</p>
          ) : (
            <p className="mt-0.5 text-xs text-text-tertiary">
              {status === 'unknown' ? 'Checking...' : 'Unavailable'}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
